import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import {AUTH_URL, PRODUCT_ID} from './const';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null); // null until we know
    const [loading, setLoading] = useState(true);

    const pickEmail = (...candidates) => {
        for (const candidate of candidates) {
            if (typeof candidate === "string" && candidate.trim()) {
                return candidate.trim();
            }
        }
        return "";
    };

    /*
    const getUserFromStorage = async () => {
        const token = localStorage.getItem("authToken");
        const company_id = localStorage.getItem("companyId");

        try{
            const response = await axios.post(
                `${AUTH_URL}validate-token`,
                { company_id: company_id, product_id: PRODUCT_ID },
                { headers: { Authorization: token } }
            );

            console.log("response data ::: ", response.data)
            updateUserData(response.data)

            //setUser({...response.data.user, user_id: response.data.user.id, allow: response.data.allow} );
        } catch (err) {
            console.error(err.response);
            setUser(false);
        }
    };



    useEffect(() => {
        getUserFromStorage()
    }, []);
    */

    useEffect(() => {
        let intervalId;
        let isRunning = false;

        // Call validate-token to prolong token TTL and optionally rotate token
        const validateAndRefresh = async () => {
            if (isRunning) return;
            isRunning = true;
            const token = localStorage.getItem("authToken");
            const company_id = localStorage.getItem("companyId");
            if (!token || !company_id) {
                setLoading(false);
                isRunning = false;
                return;
            }

            // Preload minimal user to keep UI from redirecting while validating
            if (!user) {
                setUser({ token, company_id });
            }

            try {
                const { data } = await axios.post(
                    `${AUTH_URL}validate-token`,
                    { company_id, product_id: PRODUCT_ID },
                    { headers: { Authorization: token } }
                );

                // Server may rotate/extend token; keep user data fresh
                updateUserData(data);
            } catch (err) {
                // If token is invalid/expired -> logout and redirect to sign-in
                const status = err?.response?.status;
                if (status === 401 || status === 419) {
                    try { await logout(); } finally {
                        window.location.replace("/sign-in");
                    }
                } else {
                    console.error("validate-token failed:", err?.response?.data || err.message);
                }
            } finally {
                setLoading(false);
                isRunning = false;
            }
        };

        // Run once on mount
        validateAndRefresh();

        // Run every 10 minutes
        intervalId = setInterval(validateAndRefresh, 10 * 60 * 1000);

        // Also refresh when the tab becomes visible again
        const onVisibility = () => { if (!document.hidden) validateAndRefresh(); };
        document.addEventListener("visibilitychange", onVisibility);

        return () => {
            clearInterval(intervalId);
            document.removeEventListener("visibilitychange", onVisibility);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getEmail = () => {
        const storedEmail = pickEmail(
            user?.email,
            user?.user_email,
            localStorage.getItem("user_email"),
            localStorage.getItem("pending_user_email")
        );

        return storedEmail || "user@example.com";
    };

    const updateUserData = (data, fallbackEmail = "") => {
        if (!data?.user) {
            return;
        }

        console.log("update user data ::", data);

        setUser({ ...data.user, forward: data.forward, expired: 0 });

        // Сохраняем токен и связанные данные в `localStorage`
        localStorage.setItem('authToken', data.user.token);
        localStorage.setItem('companyId', data.user.company_id);

        const normalizedEmail = pickEmail(
            data.user.email,
            data.user.user_email,
            data.email,
            data.user_email,
            fallbackEmail,
            localStorage.getItem("pending_user_email"),
            localStorage.getItem("user_email")
        );

        if (normalizedEmail) {
            localStorage.setItem('user_email', normalizedEmail);
            localStorage.removeItem('pending_user_email');
        }
    };

    const setUserData = (userData) => setUser(userData);

    const login = async (email, password, product_id = PRODUCT_ID, rememberDevice = 0, lang = "en") => {
        let responseData = {status: 200, error: ""}
        const normalizedEmail = (email || "").trim();

        if (normalizedEmail) {
            localStorage.setItem("pending_user_email", normalizedEmail);
        } else {
            localStorage.removeItem("pending_user_email");
        }

        try {
            const response = await axios.post(`${AUTH_URL}authorize`, {
                email,
                password,
                product_id,
                rememberDevice,
                lang
            });

            if (response.status === 200) {

                console.log("response data :: ", response.data)

                updateUserData(response.data, normalizedEmail)
                //setUser(response.data);

                responseData = {status: response.status, error: "", forward: response.data.forward}
            }
        } catch (err) {
            console.error('Login failed:', err.response?.data?.message || err.message);
            responseData = {status: 0, error: err.response?.data?.message || 'Login failed'}
        }

        return responseData
    }

    const loginGoogle = async (google_token, product_id = PRODUCT_ID, lang = "en") => {
        let responseData = {status: 200, error: ""}

        try {
            const response = await axios.post(`${AUTH_URL}google-authorize`, {
                token: google_token,
                product_id,
                lang
            });

            if (response.status === 200) {
                //const { token, company_id } = response.data;

                console.log("response data google auth:: ", response.data)
                updateUserData(response.data)

                responseData = {status: response.status, error: "", forward: response.data.forward}
            }
        } catch (err) {
            console.error('Login failed:', err.response?.data?.message || err.message);
            responseData = {status: 0, error: err.response?.data?.message || 'Login failed'}
        }

        return responseData
    }

    const confirm2FA = async (code) => {
        let responseData = {status: 200, error: ""}
        const token = localStorage.getItem("authToken");

        try {
            const response = await axios.post(`${AUTH_URL}confirm_2fa`,
                {code},
                {headers: { Authorization: token }});

            if (response.status === 200) {
                updateUserData(response.data)

                responseData = {...response.data, status: response.status, error: response.data.message, }
            }
        } catch (err) {
            console.error('Login failed:', err);
            responseData = {status: 0, error: err.response?.data?.message || 'Login failed'}
        }

        return responseData
    }

    const registration = async (email, password, firstName, lastName, companyName, tariffPlan, product_id = PRODUCT_ID, googleToken, lang = (localStorage.getItem('lang') || 'en')) => {
        let responseData = {status: 200, error: ""}
        const normalizedEmail = (email || "").trim();

        if (normalizedEmail) {
            localStorage.setItem("pending_user_email", normalizedEmail);
        }

        const response = await axios.post(`${AUTH_URL}registration`, {
            email,
            password,
            first_name: firstName,
            last_name: lastName,
            company_name: companyName,
            product_id: product_id,
            tariff_plan: tariffPlan,
            google_token: googleToken,
            lang
        });

        if (response.status === 200) {
            console.log("response data :: ", response.data)
            updateUserData(response.data, normalizedEmail)

            responseData = {...response.data, status: response.status, error: response.data.message, }
        }

        return responseData
    }

    const confirmRegistration = async (email, code, lang = (localStorage.getItem('lang') || 'en')) => {
        let responseData = {status: 200, error: ""}
        const token = localStorage.getItem("authToken");
        const normalizedEmail = (email || "").trim();

        const response = await axios.post(`${AUTH_URL}confirm-registration`,
            {email, code, lang},
            {headers: { Authorization: token }});

        if (response.status === 200) {
            console.log("response data :: ", response.data)
            updateUserData(response.data, normalizedEmail)

            responseData = {...response.data, status: response.status, error: response.data.message, }
            //responseData = {status: response.data.user.allow_access, error: "", forward: response.data.forward}
        }

        return responseData
    }

    const logout  = async () => {
        if (user && user.token) {
            try {
                await axios.post(`${AUTH_URL}logout`, {}, {
                    headers: { Authorization: user.token }
                });
            } catch (error) {

            }
        }

        setUser(false);
        localStorage.removeItem("authToken");
        localStorage.removeItem("companyId");
    }

    // 🔹 Fetch available workspaces for the user
    const getUserWorkspaces = async () => {
        const token = localStorage.getItem("authToken");

        try {
            const response = await axios.get(`${AUTH_URL}user/workspaces`, {
                headers: { Authorization: token },
                params: { product_id: PRODUCT_ID },
            });

            return response.data; // Expected to return { workspaces: [...] }
        } catch (err) {
            console.error("Failed to fetch workspaces:", err.response?.data?.message || err.message);
            throw new Error(err.response?.data?.message || "Could not load workspaces");
        }
    };

    // 🔹 Select a workspace and update user session
    const selectWorkspace = async (workspaceId) => {
        const token = localStorage.getItem("authToken");

        try {
            const response = await axios.post(`${AUTH_URL}user/select-workspace`,
                { workspace_id: workspaceId, product_id: PRODUCT_ID },
                { headers: { Authorization: token } });

            if (response.status === 200) {
                console.log("Workspace selected:", response.data);
                updateUserData(response.data); // Update user session
            }
        } catch (err) {
            console.error("Failed to select workspace:", err.response?.data?.message || err.message);
            throw new Error(err.response?.data?.message || "Could not select workspace");

            setUser(false);
        }
    };

    const refreshUser = async () => {
        const token = localStorage.getItem("authToken");
        const company_id = localStorage.getItem("companyId");
        if (!token || !company_id) return false;

        try {
            const { data } = await axios.post(
                `${AUTH_URL}validate-token`,
                { company_id, product_id: PRODUCT_ID },
                { headers: { Authorization: token } }
            );
            updateUserData(data);
            return true;
        } catch (err) {
            const status = err?.response?.status;
            if (status === 401 || status === 419) {
                await logout();
            }
            console.error("refreshUser failed:", err?.response?.data || err.message);
            return false;
        }
    };

    return (
        <AuthContext.Provider value={{ user, loading, setUserData, updateUserData, refreshUser, getEmail, login, confirm2FA,
            loginGoogle, getUserWorkspaces, selectWorkspace,
            registration, confirmRegistration, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
